import * as angular from 'angular';
import { BehaviorSubject } from 'rxjs';

import { DeprecatedApiService } from '@mode/legacy/data-access-model-js';
import { SelectOption, TableListData, TableListValueType } from '@mode/shared/ui-angularjs';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import {
  EntitledActionState,
  SelectFieldType,
} from '~/modules/org-settings/components/connection/connection-permissions/connection-permissions-access-dropdown/connection-permissions-access-dropdown.component';
import { ConnectionPermissionsGroupDetailService } from '~/modules/org-settings/components/connection/connection-permissions/connection-permissions-group-detail/connection-permissions-group-detail.service';
import { ConnectionPermissionsService } from '~/modules/org-settings/components/connection/connection-permissions/connection-permissions.service';
import { AppStore } from '~/types/main.types';

import { RouterContext } from '../../../../services/navigation-history-service';

class ConnectionPermissionsGroupDetailController {
  private accessOptions: EntitledActionState[] = [];
  private group: any;
  private groupDataSourceEntitlement: any;
  private groupName: any;
  private groupMembersLoaded = false;
  private groupMembersTableListData: TableListData[] = [];
  public schemeForGroupMembers = {
    fields: [
      {
        name: 'avatar',
        title: null,
        type: TableListValueType.Avatar,
      },
      {
        name: 'name',
        title: null,
        type: TableListValueType.Html,
      },
      {
        name: 'email',
        title: null,
        type: TableListValueType.Text,
      },
    ],
  };
  public groupMemberCountLabel: string;
  private currentRouterContext: RouterContext;
  private backLinkSref: string;
  backNavLink$: BehaviorSubject<any> = new BehaviorSubject(null);
  entitlementAction$: BehaviorSubject<any> = new BehaviorSubject(null);

  static $inject = [
    'ToastService',
    '$state',
    '$timeout',
    '$stateParams',
    'RxStoreService',
    '$scope',
    'NavigationHistoryService',
    'ApiService',
    'ConnectionPermissionsService',
    'ConnectionPermissionsGroupDetailService',
  ];

  constructor(
    private AlertService,
    private $state,
    private $timeout,
    private $stateParams,
    private rxStore: AppStore,
    private $scope: angular.IScope,
    private NavigationHistoryService,
    private api: DeprecatedApiService,
    private connectionPermissionsService: ConnectionPermissionsService,
    private connectionPermissionsGroupDetailService: ConnectionPermissionsGroupDetailService
  ) {
    this.currentRouterContext = NavigationHistoryService.getCurrentContext();
  }

  $onInit() {
    this.accessOptions = this.getAccessOptions();

    // if there is previous history, set fancy back link data
    if (this.currentRouterContext.fromState.name) {
      const routerContext = this.currentRouterContext;
      this.backNavLink$.next({
        stateName: routerContext.fromState.name,
        params: routerContext.fromParams,
        srefString: this.buildSrefLink(routerContext),
        text: '< Back to previous view',
      });
    } else {
      // else just set basic 'back to all members' link data
      this.backNavLink$.next({
        stateName: 'org-settings.connection.permissions.list',
        params: {},
        srefString: 'org-settings.connection.permissions.list',
        text: '< Back to All members',
      });
    }

    this.entitlementAction$.next(this.groupDataSourceEntitlement.action);
    this.connectionPermissionsGroupDetailService.getGroupMembersList(this.group).then((groupMembersList) => {
      if (groupMembersList) {
        const numberOfMembers = groupMembersList.length;

        this.groupMembersLoaded = true;
        this.groupMemberCountLabel = `(${numberOfMembers} member${numberOfMembers === 1 ? '' : 's'})`;
        this.$scope.$applyAsync(() => {
          this.groupMembersTableListData = groupMembersList.map((member) => ({ values: member }));
        });
      }
    });
  }

  handlePermissionUpdate($option: SelectOption) {
    // Remove access selected
    if ($option.value === 'none') {
      return this.removeGroupPermissionAccess();
    }
    this.updateEntitlementPermission($option.value.toString());
  }

  updateEntitlementPermission(actionType: string) {
    const editPromise = this.api.edit(this.groupDataSourceEntitlement, {
      'permission.action': actionType,
    });

    this.entitlementAction$.next(actionType);
    editPromise
      .then(() => this.connectionPermissionsService.alertAccessUpdated())
      .catch((err) => {
        this.entitlementAction$.next(this.groupDataSourceEntitlement.action);
        BugsnagHelpers.notifySafely(err);
        this.AlertService.error(`Could not change group access`);
      });
  }

  buildSrefLink({ fromState, fromParams }) {
    if (fromState.name) {
      return `${fromState.name}(${JSON.stringify(fromParams)})`;
    }
    return 'org-settings.connection.permissions.list';
  }

  removeGroupPermissionAccess() {
    try {
      this.api
        .destroy(this.groupDataSourceEntitlement, {})
        .then(() => {
          this.AlertService.success(`Removed Group. Redirecting...`);
          this.$timeout(() => {
            const { stateName, params } = this.backNavLink$.getValue();
            this.$state.go(stateName, params);
          }, 4000);
        })
        .catch((err) => {
          BugsnagHelpers.notifySafely(err);
          this.AlertService.error(`Could not remove group access`);
        });
    } catch (err) {
      BugsnagHelpers.notifySafely(err);
      this.AlertService.error(`Could not remove group access`);
    }
  }

  getAccessOptions(): EntitledActionState[] {
    return this.connectionPermissionsService.getEntitlementAccessOptions(this.groupDataSourceEntitlement);
  }

  hasGroupMembersListData() {
    return !!this.groupMembersTableListData && this.groupMembersTableListData.length;
  }
}

export const ConnectionPermissionsGroupDetailComponent: angular.IComponentOptions = {
  controller: ConnectionPermissionsGroupDetailController,
  controllerAs: 'ctrl',
  bindings: {
    group: '<',
    groupAvatar: '<',
    groupName: '<',
    groupDataSourceEntitlement: '<',
  },
  template: `
    <div class="permissions-group-detail">
      <div ng-if="(ctrl.backNavLink$ | async:this).stateName" class="tab-section-wrapper">
        <a ui-sref="{{(ctrl.backNavLink$|async:this).srefString}}">
          {{(ctrl.backNavLink$ | async:this).text}}
        </a>
      </div>

      <hr />

      <div class="detail-wrapper group-detail-wrapper">
        <section class="detail-header">
          <div class="group-name-and-controls">
            <span class="group-name page-name">
              <strong>Group:</strong>
              {{ctrl.groupName}} {{ ctrl.groupMemberCountLabel }}
            </span>
          </div>
          <div class="access-type">
            <strong>Access:</strong>
            <connection-permissions-access-dropdown
              field-type="${SelectFieldType.Inline}"
              on-change="ctrl.handlePermissionUpdate($option)"
              options="ctrl.accessOptions"
              remove-enabled="true"
              value="ctrl.entitlementAction$ | async:this"
            />
          </div>

        </section>

        <section class="detail-body">
          <div class="user-access-table group-access-group">
            <table-list
              ng-if="ctrl.hasGroupMembersListData()"
              schema="ctrl.schemeForGroupMembers"
              data="ctrl.groupMembersTableListData"
            />
            <div ng-if="!ctrl.hasGroupMembersListData()">
              {{ ctrl.groupMembersLoaded ? "None" : "Loading group members..." }}
            </div>
          </div>
        </section>
      </div>
    </div>
  `,
};
